import React, { FC, useState } from "react";
import svgIcons from "../../assets/svg/svgIcons";

type LandingFormFieldInputProps = {
  label: string;
  type: string;
  id: string;
  name: string;
  placeholder?: string;
  onChange?: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onBlur?: (e: React.FocusEvent<HTMLInputElement>) => void;
  value?: string;
  touched?: boolean;
  error?: string;
  readonly?: boolean;
  customInputClass?: string;
  disabled?: boolean;
  loading?: boolean;
};

export const LandingFormFieldInput = React.forwardRef<
  HTMLInputElement,
  React.PropsWithChildren<LandingFormFieldInputProps>
>(
  (
    {
      label,
      type,
      id,
      name,
      placeholder = "",
      onChange,
      onBlur,
      value,
      touched,
      error,
      readonly = false,
      customInputClass = "",
      disabled,
      loading = false,
    },
    ref
  ) => {
    let wrapperClass = touched && !!error ? "" : "";
    let labelClass =
      touched && !!error ? "text-field-error-dark" : "text-field-label-valid";
    let inputClass =
      touched && !!error
        ? "border-field-error-border focus-visible:outline-field-error-dark text-field-error-dark"
        : "border-gray-200 focus-visible:outline-accent-blue-pale text-field-label-valid";
    const floatingError = false;

    const [showPassword, setShowPassword] = useState(false);
    const [passwordFieldType, setPasswordFieldType] = useState(type);

    const togglePassword = () => {
      setPasswordFieldType(showPassword ? "password" : "text");
      setShowPassword(!showPassword);
    };

    return (
      <div
        className={`col-span-6 relative${floatingError ? ` pb-4` : ""} ${wrapperClass}`}
      >
        <label
          htmlFor={id}
          className={`block text-sm font-display font-semibold ${labelClass}`}
        >
          {label}
        </label>

        <input
          ref={ref}
          {...(type === "password" && { autoComplete: "off" })}
          type={type === "password" ? passwordFieldType : type}
          id={id}
          name={name}
          placeholder={placeholder}
          onChange={onChange}
          onBlur={onBlur}
          value={value}
          readOnly={readonly}
          className={`mt-1 w-full h-11 ${type === "password" || loading ? "ps-3 pe-12" : "px-3"} rounded-md bg-white read-only:bg-gray-200 read-only:border-gray-300 text-sm shadow-sm focus-visible:outline-4 focus-visible:shadow-none${touched ? " touched" : ""} ${inputClass}${type === "password" ? " tracking-widest placeholder-tracking-normal" : ""} ${customInputClass}`}
          disabled={disabled}
        />
        {loading && (
          <img
            src={svgIcons.LoadingAnimation}
            alt="loading"
            className="absolute right-3 top-11 mt-0.5 size-8 -translate-y-1/2"
          />
        )}
        {type === "password" && (
          <button
            type="button"
            onClick={togglePassword}
            className={`absolute p-2 right-3 mt-[2px] top-11 transform -translate-y-1/2 z-10`}
          >
            <img
              src={
                showPassword
                  ? svgIcons.HidePasswordIcon
                  : svgIcons.ShowPasswordIcon
              }
              alt="toggle password"
              className="size-4"
            />
          </button>
        )}
        {touched && !!error && (
          <p
            className={`${floatingError ? "absolute" : ""} bg-field-error-light px-2 py-1 rounded text-field-error-dark text-xs mt-1`}
          >
            {error}
          </p>
        )}
      </div>
    );
  }
);

type LandingFormFieldSubmitProps = {
  type?: "submit" | "button";
  variant?: "primary" | "secondary";
  label: string;
  onClick?: () => void;
  disabled?: boolean;
};

export const LandingFormFieldSubmit = React.forwardRef<
  HTMLButtonElement,
  React.PropsWithChildren<LandingFormFieldSubmitProps>
>(
  (
    {
      type = "submit",
      variant = "secondary",
      label,
      onClick,
      disabled = false,
    },
    ref
  ) => {
    let variantClass = "";
    const disabledClass = "border-gray-200 bg-gray-200 text-gray-400";
    switch (variant) {
      case "primary":
        variantClass = disabled
          ? disabledClass
          : "border-accent-blue-pale bg-accent-blue-pale text-accent-blue-dark enabled:hover:bg-accent-blue-dark enabled:hover:text-white enabled:hover:bg-accent-blue-dark active:bg-accent-blue-pale";
        break;
      default: // secondary
        variantClass = disabled
          ? disabledClass
          : (variantClass =
              "border-accent-blue-dark bg-transparent text-accent-blue-dark enabled:hover:bg-accent-blue-pale active:bg-accent-blue-dark active:text-accent-blue-pale");
    }
    return (
      <div className="col-span-6">
        <button
          ref={ref}
          className={`h-12 w-full rounded-lg border text-sm font-display font-semibold transition focus:outline-none focus:ring ${variantClass}`}
          type={type}
          onClick={onClick}
          disabled={disabled}
        >
          {label}
        </button>
      </div>
    );
  }
);
